// @ts-nocheck
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useReducer,
} from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClassProperty {
  name: string;
  type: string;
}

interface ClassMethod {
  name: string;
}

interface ClassNode {
  id: string;
  label: string;
  parent: string | null;
  properties: ClassProperty[];
  methods: ClassMethod[];
  x: number;
  y: number;
}

interface ObjectProp {
  name: string;
  domain: string;
  range: string;
}

interface DiagramState {
  classes: Record<string, ClassNode>;
  objectProps: Record<string, ObjectProp>;
}

type EditCellInfo = {
  classId: string;
  section: "name" | "props" | "methods";
  index: number;
  field: string;
};

type Action =
  | { type: "REPLACE_ALL"; state: DiagramState }
  | { type: "MOVE_CLASS"; id: string; x: number; y: number }
  | { type: "ADD_CLASS" }
  | { type: "DELETE_CLASS"; id: string }
  | { type: "EDIT_CELL"; classId: string; section: string; index: number; field: string; value: string }
  | { type: "ADD_PROP"; classId: string }
  | { type: "DELETE_PROP"; classId: string; index: number }
  | { type: "DELETE_METHOD"; classId: string; index: number }
  | { type: "ADD_OBJECT_PROP"; domain: string; range: string };

// ─── Constants ────────────────────────────────────────────────────────────────

const CLASS_W = 210;
const HEADER_H = 44;
const ROW_H = 24;
const SEC_H = 28;

function classHeight(cls: ClassNode): number {
  return (
    HEADER_H +
    SEC_H +
    (cls.properties.length === 0 ? 28 : cls.properties.length * ROW_H + 8) +
    SEC_H +
    (cls.methods.length === 0 ? 24 : cls.methods.length * ROW_H + 4)
  );
}

// ─── Default TTL ──────────────────────────────────────────────────────────────

const DEFAULT_TTL = `@prefix rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#> .
@prefix rdfs: <http://www.w3.org/2000/01/rdf-schema#> .
@prefix owl: <http://www.w3.org/2002/07/owl#> .
@prefix xsd: <http://www.w3.org/2001/XMLSchema#> .
@prefix ex: <http://example.org/ontology#> .

# Classes

ex:Person a owl:Class ;
    rdfs:label "Person" .

ex:Student a owl:Class ;
    rdfs:subClassOf ex:Person ;
    rdfs:label "Student" .

ex:Teacher a owl:Class ;
    rdfs:subClassOf ex:Person ;
    rdfs:label "Teacher" .

ex:Course a owl:Class ;
    rdfs:label "Course" .

ex:Organization a owl:Class ;
    rdfs:label "Organization" .

# Properties

ex:hasName a owl:DatatypeProperty ;
    rdfs:domain ex:Person ;
    rdfs:range xsd:string ;
    rdfs:label "hasName" .

ex:hasAge a owl:DatatypeProperty ;
    rdfs:domain ex:Person ;
    rdfs:range xsd:integer ;
    rdfs:label "hasAge" .

ex:studentID a owl:DatatypeProperty ;
    rdfs:domain ex:Student ;
    rdfs:range xsd:string ;
    rdfs:label "studentID" .

ex:teaches a owl:ObjectProperty ;
    rdfs:domain ex:Teacher ;
    rdfs:range ex:Course ;
    rdfs:label "teaches" .

ex:enrolledIn a owl:ObjectProperty ;
    rdfs:domain ex:Student ;
    rdfs:range ex:Course ;
    rdfs:label "enrolledIn" .

ex:worksFor a owl:ObjectProperty ;
    rdfs:domain ex:Person ;
    rdfs:range ex:Organization ;
    rdfs:label "worksFor" .`;

// ─── Turtle Parser ────────────────────────────────────────────────────────────

function parseTurtle(ttl: string): { classes: Record<string, ClassNode>; objectProps: Record<string, ObjectProp> } {
  const prefixes: Record<string, string> = {};
  const classes: Record<string, ClassNode> = {};
  const objectProps: Record<string, ObjectProp> = {};

  const prefixRe = /@prefix\s+(\w*):\s*<([^>]+)>\s*\./g;
  let m: RegExpExecArray | null;
  while ((m = prefixRe.exec(ttl)) !== null) {
    prefixes[m[1]] = m[2];
  }

  const resolve = (curie: string): string => {
    if (!curie) return "";
    const i = curie.indexOf(":");
    if (i < 0) return curie;
    const prefix = curie.slice(0, i);
    const local = curie.slice(i + 1);
    return prefixes[prefix] ? prefixes[prefix] + local : curie;
  };

  const localName = (uri: string): string => {
    if (!uri) return uri;
    const hash = uri.lastIndexOf("#");
    const slash = uri.lastIndexOf("/");
    return uri.slice(Math.max(hash, slash) + 1);
  };

  const normalised = ttl.replace(/#[^\n]*/g, "").replace(/\s+/g, " ");

  // Classes with subClassOf + label
  const classRe =
    /(\w+:\w+)\s+(?:a|rdf:type)\s+owl:Class\s*;(?:[^.]*rdfs:subClassOf\s+(\w+:\w+))?[^.]*rdfs:label\s+"([^"]+)"[^.]*/g;
  while ((m = classRe.exec(normalised)) !== null) {
    const id = localName(resolve(m[1]));
    classes[id] = { id, label: m[3] || id, parent: m[2] ? localName(resolve(m[2])) : null, properties: [], methods: [], x: 0, y: 0 };
  }

  // Fallback: owl:Class without label
  const classRe2 = /(\w+:\w+)\s+(?:a|rdf:type)\s+owl:Class(?:\s*;[^.]*rdfs:label\s+"([^"]+)")?/g;
  while ((m = classRe2.exec(normalised)) !== null) {
    const id = localName(resolve(m[1]));
    if (!classes[id]) {
      classes[id] = { id, label: m[2] || id, parent: null, properties: [], methods: [], x: 0, y: 0 };
    }
  }

  // DatatypeProperty
  const dtRe =
    /(\w+:\w+)\s+(?:a|rdf:type)\s+owl:DatatypeProperty\s*;[^.]*rdfs:domain\s+(\w+:\w+)[^.]*rdfs:range\s+(\w+:\w+)[^.]*rdfs:label\s+"([^"]+)"/g;
  while ((m = dtRe.exec(normalised)) !== null) {
    const domain = localName(resolve(m[2]));
    const range = localName(resolve(m[3]));
    if (classes[domain]) {
      classes[domain].properties.push({ name: m[4] || localName(resolve(m[1])), type: range });
    }
  }

  // ObjectProperty
  const opRe =
    /(\w+:\w+)\s+(?:a|rdf:type)\s+owl:ObjectProperty\s*;[^.]*rdfs:domain\s+(\w+:\w+)[^.]*rdfs:range\s+(\w+:\w+)[^.]*rdfs:label\s+"([^"]+)"/g;
  while ((m = opRe.exec(normalised)) !== null) {
    const name = m[4] || localName(resolve(m[1]));
    const domain = localName(resolve(m[2]));
    const range = localName(resolve(m[3]));
    objectProps[name] = { name, domain, range };
    if (classes[domain]) {
      classes[domain].methods.push({ name: `+${name}()` });
    }
  }

  return { classes, objectProps };
}

// ─── Turtle Serialiser ────────────────────────────────────────────────────────

function serialiseTurtle(state: DiagramState): string {
  const { classes, objectProps } = state;
  const lines: string[] = [];
  lines.push(`@prefix rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#> .`);
  lines.push(`@prefix rdfs: <http://www.w3.org/2000/01/rdf-schema#> .`);
  lines.push(`@prefix owl: <http://www.w3.org/2002/07/owl#> .`);
  lines.push(`@prefix xsd: <http://www.w3.org/2001/XMLSchema#> .`);
  lines.push(`@prefix ex: <http://example.org/ontology#> .`);
  lines.push(``);
  lines.push(`# Classes`);
  Object.values(classes).forEach((cls) => {
    lines.push(``);
    lines.push(`ex:${cls.id} a owl:Class ;`);
    if (cls.parent) lines.push(`    rdfs:subClassOf ex:${cls.parent} ;`);
    lines.push(`    rdfs:label "${cls.label}" .`);
  });
  lines.push(``);
  lines.push(`# Properties`);
  Object.values(classes).forEach((cls) => {
    cls.properties.forEach((prop) => {
      const safeName = prop.name.replace(/\s+/g, "_");
      const xsdType = prop.type === "integer" || prop.type === "int" ? "xsd:integer" : "xsd:string";
      lines.push(``);
      lines.push(`ex:${safeName} a owl:DatatypeProperty ;`);
      lines.push(`    rdfs:domain ex:${cls.id} ;`);
      lines.push(`    rdfs:range ${xsdType} ;`);
      lines.push(`    rdfs:label "${prop.name}" .`);
    });
  });
  Object.values(objectProps).forEach((op) => {
    const safeName = op.name.replace(/\s+/g, "_");
    lines.push(``);
    lines.push(`ex:${safeName} a owl:ObjectProperty ;`);
    lines.push(`    rdfs:domain ex:${op.domain} ;`);
    lines.push(`    rdfs:range ex:${op.range} ;`);
    lines.push(`    rdfs:label "${op.name}" .`);
  });
  return lines.join("\n");
}

// ─── Auto-layout ──────────────────────────────────────────────────────────────

function autoLayout(classes: Record<string, ClassNode>): Record<string, ClassNode> {
  const ids = Object.keys(classes);
  const cols = Math.ceil(Math.sqrt(ids.length)) || 1;
  const W = 240, H = 200, GAP_X = 80, GAP_Y = 60;
  ids.forEach((id, i) => {
    classes[id].x = 60 + (i % cols) * (W + GAP_X);
    classes[id].y = 60 + Math.floor(i / cols) * (H + GAP_Y);
  });
  return classes;
}

// ─── Reducer ──────────────────────────────────────────────────────────────────

function reducer(state: DiagramState, action: Action): DiagramState {
  const cloneClass = (id: string): ClassNode => ({ ...state.classes[id], properties: [...state.classes[id].properties], methods: [...state.classes[id].methods] });

  switch (action.type) {
    case "REPLACE_ALL":
      return action.state;

    case "MOVE_CLASS":
      return { ...state, classes: { ...state.classes, [action.id]: { ...state.classes[action.id], x: Math.max(0, action.x), y: Math.max(0, action.y) } } };

    case "ADD_CLASS": {
      const id = `Class${Date.now()}`;
      return { ...state, classes: { ...state.classes, [id]: { id, label: "NewClass", parent: null, properties: [], methods: [], x: 80, y: 80 } } };
    }

    case "DELETE_CLASS": {
      const { [action.id]: _removed, ...rest } = state.classes;
      const ops = Object.fromEntries(Object.entries(state.objectProps).filter(([, op]) => op.domain !== action.id && op.range !== action.id));
      return { ...state, classes: rest, objectProps: ops };
    }

    case "EDIT_CELL": {
      const { classId, section, index, field, value } = action;
      if (section === "name") {
        const renamed = cloneClass(classId);
        renamed.label = value;
        const newId = value.replace(/\s+/g, "_") || classId;
        const { [classId]: _old, ...restCls } = state.classes;
        restCls[newId] = { ...renamed, id: newId };
        Object.values(restCls).forEach((c) => { if (c.parent === classId) c.parent = newId; });
        const ops = Object.fromEntries(
          Object.entries(state.objectProps).map(([k, op]) => [k, {
            ...op,
            domain: op.domain === classId ? newId : op.domain,
            range: op.range === classId ? newId : op.range,
          }])
        );
        return { ...state, classes: restCls, objectProps: ops };
      }
      if (section === "props") {
        const c = cloneClass(classId);
        c.properties[index] = { ...c.properties[index], [field]: value };
        return { ...state, classes: { ...state.classes, [classId]: c } };
      }
      return state;
    }

    case "ADD_PROP": {
      const c = cloneClass(action.classId);
      c.properties.push({ name: "newProp", type: "string" });
      return { ...state, classes: { ...state.classes, [action.classId]: c } };
    }

    case "DELETE_PROP": {
      const c = cloneClass(action.classId);
      c.properties = c.properties.filter((_, i) => i !== action.index);
      return { ...state, classes: { ...state.classes, [action.classId]: c } };
    }

    case "DELETE_METHOD": {
      const c = cloneClass(action.classId);
      c.methods = c.methods.filter((_, i) => i !== action.index);
      return { ...state, classes: { ...state.classes, [action.classId]: c } };
    }

    case "ADD_OBJECT_PROP": {
      const name = `rel_${action.domain}_${action.range}`;
      return { ...state, objectProps: { ...state.objectProps, [name]: { name, domain: action.domain, range: action.range } } };
    }

    default:
      return state;
  }
}

// ─── Turtle Editor ────────────────────────────────────────────────────────────

interface TurtleEditorProps {
  ttl: string;
  onChange: (val: string) => void;
}

const LINE_H = 20; // px — must match lineHeight below

const TurtleEditor: React.FC<TurtleEditorProps> = ({ ttl, onChange }) => {
  const taRef    = useRef<HTMLTextAreaElement>(null);
  const lnRef    = useRef<HTMLDivElement>(null);
  const lineCount = ttl.split("\n").length;

  // Sync line-number gutter scroll to textarea scroll
  const handleScroll = () => {
    if (lnRef.current && taRef.current) {
      lnRef.current.scrollTop = taRef.current.scrollTop;
    }
  };

  const FONT = "'JetBrains Mono', 'Fira Code', 'Courier New', monospace";

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden", fontFamily: FONT, fontSize: 12, background: "#fafbfc" }}>

      {/* Line-number gutter — scrolls in sync, pointer-events:none so clicks go to textarea */}
      <div
        ref={lnRef}
        aria-hidden="true"
        style={{
          width: 44,
          flexShrink: 0,
          overflowY: "hidden",   // hidden — synced via JS
          overflowX: "hidden",
          paddingTop: 12,
          paddingBottom: 12,
          lineHeight: `${LINE_H}px`,
          textAlign: "right",
          paddingRight: 8,
          fontSize: 11,
          color: "#cbd5e1",
          userSelect: "none",
          background: "#f1f5f9",
          borderRight: "1px solid #e2e8f0",
          boxSizing: "border-box",
        }}
      >
        {Array.from({ length: lineCount }, (_, i) => (
          <div key={i} style={{ height: LINE_H }}>{i + 1}</div>
        ))}
      </div>

      {/* Editable textarea — fully scrollable */}
      <textarea
        ref={taRef}
        value={ttl}
        onChange={(e) => onChange(e.target.value)}
        onScroll={handleScroll}
        spellCheck={false}
        style={{
          flex: 1,
          height: "100%",
          padding: "12px 16px",
          lineHeight: `${LINE_H}px`,
          fontFamily: FONT,
          fontSize: 12,
          background: "#fafbfc",
          border: "none",
          outline: "none",
          resize: "none",
          color: "#1e293b",
          caretColor: "#1e293b",
          overflowY: "auto",
          overflowX: "auto",
          whiteSpace: "pre",
          overflowWrap: "normal",
          boxSizing: "border-box",
          tabSize: 4,
        }}
      />
    </div>
  );
};

// ─── Diagram Canvas ───────────────────────────────────────────────────────────

interface DiagramCanvasProps {
  state: DiagramState;
  dispatch: React.Dispatch<Action>;
}

const DiagramCanvas: React.FC<DiagramCanvasProps> = ({ state, dispatch }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<{ id: string; ox: number; oy: number } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<EditCellInfo | null>(null);
  const [editVal, setEditVal] = useState<string>("");
  const [connectMode, setConnectMode] = useState<boolean>(false);
  const [connectFrom, setConnectFrom] = useState<string | null>(null);

  const { classes, objectProps } = state;

  // ── Drag ──
  const onSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!dragRef.current || !svgRef.current) return;
    const { id, ox, oy } = dragRef.current;
    const rect = svgRef.current.getBoundingClientRect();
    dispatch({ type: "MOVE_CLASS", id, x: e.clientX - rect.left - ox, y: e.clientY - rect.top - oy });
  };

  const onSvgMouseUp = () => { dragRef.current = null; };

  const startDrag = (e: React.MouseEvent, id: string) => {
    if (editingCell || connectMode) return;
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const cls = classes[id];
    dragRef.current = { id, ox: e.clientX - rect.left - cls.x, oy: e.clientY - rect.top - cls.y };
    setSelected(id);
    e.stopPropagation();
  };

  // ── Inline edit ──
  const beginEdit = (e: React.MouseEvent, cellInfo: EditCellInfo, currentVal: string) => {
    e.stopPropagation();
    dragRef.current = null;
    setEditingCell(cellInfo);
    setEditVal(currentVal);
  };

  const commitEdit = () => {
    if (!editingCell) return;
    dispatch({ type: "EDIT_CELL", classId: editingCell.classId, section: editingCell.section, index: editingCell.index, field: editingCell.field, value: editVal.trim() || editVal });
    setEditingCell(null);
  };

  // ── Connection ──
  const handleHeaderClick = (e: React.MouseEvent, id: string) => {
    if (!connectMode) return;
    e.stopPropagation();
    if (!connectFrom) { setConnectFrom(id); return; }
    if (connectFrom !== id) {
      dispatch({ type: "ADD_OBJECT_PROP", domain: connectFrom, range: id });
      setConnectFrom(null);
      setConnectMode(false);
    }
  };

  const svgClick = () => {
    if (!connectMode) setSelected(null);
    setEditingCell(null);
  };

  // ── Arrow rendering ──
  const arrows: React.ReactNode[] = [];

  Object.values(classes).forEach((cls) => {
    if (cls.parent && classes[cls.parent]) {
      const from = cls;
      const to = classes[cls.parent];
      const fx = from.x + CLASS_W / 2;
      const fy = from.y;
      const tx = to.x + CLASS_W / 2;
      const ty = to.y + classHeight(to);
      const my = (fy + ty) / 2;
      arrows.push(
        <path key={`inh-${cls.id}`}
          d={`M${fx},${fy} C${fx},${my} ${tx},${my} ${tx},${ty}`}
          fill="none" stroke="#10b981" strokeWidth={1.5} markerEnd="url(#arrowInherit)"
        />
      );
    }
  });

  Object.values(objectProps).forEach((op) => {
    const from = classes[op.domain];
    const to = classes[op.range];
    if (!from || !to) return;
    const fromCx = from.x + CLASS_W / 2;
    const toCx = to.x + CLASS_W / 2;
    const fromCy = from.y + classHeight(from) / 2;
    const toCy = to.y + classHeight(to) / 2;

    let x1: number, y1: number, x2: number, y2: number;
    if (toCx > fromCx + CLASS_W / 2) { x1 = from.x + CLASS_W; y1 = fromCy; x2 = to.x; y2 = toCy; }
    else if (toCx < fromCx - CLASS_W / 2) { x1 = from.x; y1 = fromCy; x2 = to.x + CLASS_W; y2 = toCy; }
    else if (toCy > fromCy) { x1 = fromCx; y1 = from.y + classHeight(from); x2 = toCx; y2 = to.y; }
    else { x1 = fromCx; y1 = from.y; x2 = toCx; y2 = to.y + classHeight(to); }

    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    arrows.push(
      <g key={`op-${op.name}`}>
        <path d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`}
          fill="none" stroke="#0ea5e9" strokeWidth={1.5} markerEnd="url(#arrowObj)" />
        <text x={mx} y={my - 6} fontSize={10} fill="#0ea5e9" textAnchor="middle" style={{ userSelect: "none" }}>{op.name}</text>
      </g>
    );
  });

  // ── Class box renderer ──
  const renderClass = (cls: ClassNode) => {
    const h = classHeight(cls);
    const isSelected = selected === cls.id;
    const isConnectSource = connectFrom === cls.id;
    const propAreaY = HEADER_H + SEC_H;
    const propCount = cls.properties.length;
    const methAreaY = propAreaY + (propCount === 0 ? 28 : propCount * ROW_H + 8) + SEC_H;

    return (
      <g key={cls.id} transform={`translate(${cls.x},${cls.y})`}
        style={{ cursor: connectMode ? "crosshair" : "default" }}
      >
        {/* Drop shadow */}
        <rect x={3} y={3} width={CLASS_W} height={h} rx={6} fill="rgba(0,0,0,0.07)" />

        {/* Box border */}
        <rect
          width={CLASS_W} height={h} rx={6}
          fill="white"
          stroke={isSelected ? "#0ea5e9" : isConnectSource ? "#10b981" : "#c8d6e5"}
          strokeWidth={isSelected || isConnectSource ? 2 : 1.2}
          onMouseDown={(e) => startDrag(e, cls.id)}
          style={{ cursor: connectMode ? "crosshair" : "grab" }}
        />

        {/* Header bg */}
        <rect width={CLASS_W} height={HEADER_H} rx={6} fill="#f8fafc"
          onMouseDown={(e) => startDrag(e, cls.id)}
          onClick={(e) => handleHeaderClick(e, cls.id)}
          style={{ cursor: connectMode ? "crosshair" : "grab" }}
        />
        <rect y={HEADER_H - 1} width={CLASS_W} height={1} fill="#e2e8f0" />

        {/* Class name */}
        {editingCell?.classId === cls.id && editingCell?.section === "name" ? (
          <foreignObject x={8} y={10} width={CLASS_W - 16} height={26}>
            <input
              autoFocus
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") setEditingCell(null); }}
              style={{ width: "100%", border: "1px solid #0ea5e9", borderRadius: 3, padding: "2px 6px", fontSize: 13, fontWeight: 700, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
            />
          </foreignObject>
        ) : (
          <text
            x={CLASS_W / 2} y={HEADER_H / 2 + 5}
            textAnchor="middle" fontSize={13} fontWeight="700" fill="#1e293b"
            onMouseDown={(e) => startDrag(e, cls.id)}
            onDoubleClick={(e) => beginEdit(e, { classId: cls.id, section: "name", index: -1, field: "label" }, cls.label)}
            style={{ userSelect: "none", cursor: connectMode ? "crosshair" : "text" }}
          >{cls.label}</text>
        )}

        {/* PROPERTIES section header */}
        <rect y={HEADER_H} width={CLASS_W} height={SEC_H} fill="#f1f5f9" />
        <text x={8} y={HEADER_H + 18} fontSize={10} fill="#64748b" fontWeight="600" style={{ userSelect: "none" }}>PROPERTIES</text>
        <rect y={HEADER_H + SEC_H - 1} width={CLASS_W} height={1} fill="#e2e8f0" />

        {/* Property rows */}
        {cls.properties.length === 0 ? (
          <text x={12} y={propAreaY + 18} fontSize={11} fill="#94a3b8" style={{ userSelect: "none" }}>No attributes</text>
        ) : cls.properties.map((prop, i) => {
          const py = propAreaY + i * ROW_H + 18;
          const editingName = editingCell?.classId === cls.id && editingCell?.section === "props" && editingCell?.index === i && editingCell?.field === "name";
          const editingType = editingCell?.classId === cls.id && editingCell?.section === "props" && editingCell?.index === i && editingCell?.field === "type";
          return (
            <g key={i}>
              {editingName ? (
                <foreignObject x={12} y={py - 15} width={98} height={22}>
                  <input autoFocus value={editVal} onChange={e => setEditVal(e.target.value)}
                    onBlur={commitEdit} onKeyDown={e => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") setEditingCell(null); }}
                    style={{ width: "100%", border: "1px solid #0ea5e9", borderRadius: 2, padding: "1px 4px", fontSize: 11, outline: "none" }} />
                </foreignObject>
              ) : (
                <text x={12} y={py} fontSize={11} fill="#475569"
                  onDoubleClick={e => beginEdit(e, { classId: cls.id, section: "props", index: i, field: "name" }, prop.name)}
                  style={{ userSelect: "none", cursor: "text" }}>+{prop.name}:</text>
              )}
              {editingType ? (
                <foreignObject x={114} y={py - 15} width={72} height={22}>
                  <input autoFocus value={editVal} onChange={e => setEditVal(e.target.value)}
                    onBlur={commitEdit} onKeyDown={e => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") setEditingCell(null); }}
                    style={{ width: "100%", border: "1px solid #0ea5e9", borderRadius: 2, padding: "1px 4px", fontSize: 11, outline: "none" }} />
                </foreignObject>
              ) : (
                <text x={114} y={py} fontSize={11} fill="#0ea5e9"
                  onDoubleClick={e => beginEdit(e, { classId: cls.id, section: "props", index: i, field: "type" }, prop.type)}
                  style={{ userSelect: "none", cursor: "text" }}>{prop.type}</text>
              )}
              <text x={CLASS_W - 14} y={py} fontSize={12} fill="#ef4444"
                onClick={e => { e.stopPropagation(); dispatch({ type: "DELETE_PROP", classId: cls.id, index: i }); }}
                style={{ cursor: "pointer", userSelect: "none" }}>×</text>
            </g>
          );
        })}

        {/* + Add Property */}
        <text
          x={12}
          y={propAreaY + (cls.properties.length === 0 ? 28 : cls.properties.length * ROW_H + 8) - 2}
          fontSize={11} fill="#10b981"
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={e => { e.stopPropagation(); dispatch({ type: "ADD_PROP", classId: cls.id }); }}
        >+ Add Property</text>

        {/* METHODS section header */}
        <rect y={methAreaY - SEC_H} width={CLASS_W} height={SEC_H} fill="#f1f5f9" />
        <text x={8} y={methAreaY - SEC_H + 18} fontSize={10} fill="#64748b" fontWeight="600" style={{ userSelect: "none" }}>METHODS</text>
        <rect y={methAreaY - 1} width={CLASS_W} height={1} fill="#e2e8f0" />

        {/* Method rows */}
        {cls.methods.length === 0 ? (
          <text x={12} y={methAreaY + 18} fontSize={11} fill="#94a3b8" style={{ userSelect: "none" }}>No methods</text>
        ) : cls.methods.map((method, i) => (
          <g key={i}>
            <text x={12} y={methAreaY + i * ROW_H + 18} fontSize={11} fill="#475569" style={{ userSelect: "none" }}>{method.name}</text>
            <text x={CLASS_W - 14} y={methAreaY + i * ROW_H + 18} fontSize={12} fill="#ef4444"
              onClick={e => { e.stopPropagation(); dispatch({ type: "DELETE_METHOD", classId: cls.id, index: i }); }}
              style={{ cursor: "pointer", userSelect: "none" }}>×</text>
          </g>
        ))}

        {/* Delete class (shows when selected) */}
        {isSelected && (
          <g onClick={e => { e.stopPropagation(); dispatch({ type: "DELETE_CLASS", id: cls.id }); setSelected(null); }} style={{ cursor: "pointer" }}>
            <rect x={CLASS_W - 24} y={4} width={18} height={18} rx={4} fill="#ef4444" />
            <text x={CLASS_W - 15} y={16} fontSize={11} fill="white" textAnchor="middle" style={{ userSelect: "none" }}>×</text>
          </g>
        )}
      </g>
    );
  };

  const svgW = Math.max(1200, ...Object.values(classes).map(c => c.x + CLASS_W + 100));
  const svgH = Math.max(800, ...Object.values(classes).map(c => c.y + classHeight(c) + 100));

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, padding: "8px 12px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", flexShrink: 0, alignItems: "center" }}>
        <button
          onClick={() => dispatch({ type: "ADD_CLASS" })}
          style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid #0ea5e9", background: "#0ea5e9", color: "white", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
        >+ Add Class</button>
        <button
          onClick={() => { setConnectMode(!connectMode); setConnectFrom(null); }}
          style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid", borderColor: connectMode ? "#10b981" : "#94a3b8", background: connectMode ? "#10b981" : "white", color: connectMode ? "white" : "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
        >{connectMode ? (connectFrom ? "→ Click target" : "Click source class") : "🔗 Add Relation"}</button>
        {connectMode && (
          <button onClick={() => { setConnectMode(false); setConnectFrom(null); }}
            style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #ef4444", background: "white", color: "#ef4444", fontSize: 12, cursor: "pointer" }}>
            Cancel
          </button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8" }}>
          Double-click to edit · Drag to move · Select → × to delete
        </span>
      </div>

      {/* Canvas */}
      <div style={{ flex: 1, overflow: "auto", background: "#f0f4f8", position: "relative" }}>
        {/* Dot grid bg */}
        <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width="100%" height="100%">
          <defs>
            <pattern id="ontoDots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="#cbd5e1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#ontoDots)" />
        </svg>

        <svg
          ref={svgRef}
          width={svgW}
          height={svgH}
          style={{ display: "block", position: "relative" }}
          onMouseMove={onSvgMouseMove}
          onMouseUp={onSvgMouseUp}
          onClick={svgClick}
        >
          <defs>
            <marker id="arrowInherit" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
              <path d="M0,0 L0,10 L10,5 Z" fill="none" stroke="#10b981" strokeWidth="1.5" />
            </marker>
            <marker id="arrowObj" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <path d="M0,0 L10,3.5 L0,7" fill="none" stroke="#0ea5e9" strokeWidth="1.5" />
            </marker>
          </defs>
          {arrows}
          {Object.values(classes).map(renderClass)}
        </svg>
      </div>
    </div>
  );
};

// ─── Main Exported Component ──────────────────────────────────────────────────

interface OntologyDiagramProps {
  /** Initial TTL content — if not provided, uses built-in example */
  initialTtl?: string;
  /** Called whenever the TTL changes (from either side) */
  onTtlChange?: (ttl: string) => void;
}

const OntologyDiagram: React.FC<OntologyDiagramProps> = ({ initialTtl, onTtlChange }) => {
  const startTtl = initialTtl || DEFAULT_TTL;

  const [ttl, setTtl] = useState<string>(startTtl);
  const [diagramState, dispatch] = useReducer(reducer, undefined, () => {
    const parsed = parseTurtle(startTtl);
    return { classes: autoLayout({ ...parsed.classes }), objectProps: parsed.objectProps };
  });

  /**
   * Re-seed both the TTL editor and the diagram when initialTtl arrives
   * asynchronously (e.g. after a DB fetch completes).
   * We only re-seed when initialTtl is a non-empty string that differs from
   * what we currently show — this avoids wiping in-progress edits.
   */
  const prevInitialTtlRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (
      initialTtl &&
      initialTtl.trim().length > 0 &&
      initialTtl !== prevInitialTtlRef.current
    ) {
      prevInitialTtlRef.current = initialTtl;
      setTtl(initialTtl);
      try {
        const parsed = parseTurtle(initialTtl);
        dispatch({
          type: "REPLACE_ALL",
          state: {
            classes: autoLayout({ ...parsed.classes }),
            objectProps: parsed.objectProps,
          },
        });
      } catch (_) { /* ignore malformed TTL */ }
    }
  }, [initialTtl]);

  // Diagram → TTL
  const wrappedDispatch = useCallback((action: Action) => {
    dispatch(action);
    if (action.type !== "REPLACE_ALL") {
      // We need the new state to serialise; compute it here
      const next = reducer(diagramState, action);
      const newTtl = serialiseTurtle(next);
      setTtl(newTtl);
      onTtlChange?.(newTtl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagramState, onTtlChange]);

  // TTL → Diagram
  const handleTtlChange = useCallback((newTtl: string) => {
    setTtl(newTtl);
    onTtlChange?.(newTtl);
    try {
      const parsed = parseTurtle(newTtl);
      if (Object.keys(parsed.classes).length === 0) return;
      // Preserve positions of existing classes
      const newClasses: Record<string, ClassNode> = { ...parsed.classes };
      Object.keys(newClasses).forEach((id) => {
        if (diagramState.classes[id]) {
          newClasses[id].x = diagramState.classes[id].x;
          newClasses[id].y = diagramState.classes[id].y;
        }
      });
      // Auto-layout truly new classes
      const unpositioned = Object.keys(newClasses).filter((id) => !diagramState.classes[id]);
      if (unpositioned.length > 0) {
        const all = Object.keys(newClasses);
        const cols = Math.ceil(Math.sqrt(all.length)) || 1;
        unpositioned.forEach((id) => {
          const idx = all.indexOf(id);
          newClasses[id].x = 60 + (idx % cols) * 320;
          newClasses[id].y = 60 + Math.floor(idx / cols) * 260;
        });
      }
      dispatch({ type: "REPLACE_ALL", state: { classes: newClasses, objectProps: parsed.objectProps } });
    } catch (_) { /* ignore parse errors mid-typing */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagramState.classes]);

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden", fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      {/* Left: Class Diagram */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", borderRight: "1px solid #e2e8f0" }}>
        <div style={{ padding: "8px 14px 6px", background: "white", borderBottom: "1px solid #e2e8f0", flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Class Diagram</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>Visual representation of the ontology</div>
        </div>
        <DiagramCanvas state={diagramState} dispatch={wrappedDispatch} />
      </div>

      {/* Right: TTL Editor */}
      <div style={{ width: "44%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "8px 14px 6px", background: "white", borderBottom: "1px solid #e2e8f0", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Turtle Syntax (TTL)</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Edit the TTL definition</div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => {
                const blob = new Blob([ttl], { type: "text/turtle" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "ontology.ttl"; a.click();
                URL.revokeObjectURL(url);
              }}
              style={{ padding: "3px 10px", borderRadius: 5, border: "1px solid #e2e8f0", background: "white", color: "#64748b", fontSize: 11, cursor: "pointer" }}
            >Export TTL</button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "hidden" }}>
          <TurtleEditor ttl={ttl} onChange={handleTtlChange} />
        </div>
      </div>
    </div>
  );
};

export default OntologyDiagram;